import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getNotifications, setReadNotificationStatus } from '../../services/utilityServices';

interface Notification {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  status: string;
  day: string;
  read: boolean;
  date: Date; // Added for sorting
}

export const NotificationPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'All' | 'Unread'>(t('all', 'All') as 'All' | 'Unread');
  const [data, setData] = useState<Notification[]>([]);
  const queryClient = useQueryClient();

  const { data: notificationsData } = useQuery({
    queryKey: ['getNotifications'],
    queryFn: async () => {
      const response = await getNotifications();
      return response.data || [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const { mutate: markNotificationsAsRead } = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const payload = {
        notifications: notificationIds.map((id) => ({
          notificationId: id,
          isRead: true,
        })),
      };
      const response = await setReadNotificationStatus(payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getNotifications'] });
    },
    onError: () => {
      console.error(t('failedToMarkRead', 'Failed to mark notifications as read'));
    },
  });

  const categorizeDate = (date: Date): string => {
    const notificationDate = moment(date);
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'days').startOf('day');
    const sevenDaysAgo = moment().subtract(7, 'days').startOf('day');
    const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');

    if (notificationDate.isSame(today, 'day')) {
      return t('today', 'Today');
    } else if (notificationDate.isSame(yesterday, 'day')) {
      return t('yesterday', 'Yesterday');
    } else if (notificationDate.isAfter(sevenDaysAgo)) {
      return t('previous7Days', 'Previous 7 days');
    } else if (notificationDate.isAfter(thirtyDaysAgo)) {
      return t('previous30Days', 'Previous 30 days');
    } else {
      return t('older', 'Older');
    }
  };

  useEffect(() => {
    if ((notificationsData ?? []).length > 0) {
      const transformedData = (notificationsData ?? []).map((notification) => {
        const createdDate = new Date(notification.createdDate);
        return {
          id: notification.notificationId,
          title: notification.title,
          description: notification.message,
          timestamp: moment(createdDate).format('hh:mm A'),
          status: 'default',
          day: categorizeDate(createdDate),
          read: notification.isRead,
          date: createdDate,
        };
      });
      transformedData.sort((a, b) => b.date.getTime() - a.date.getTime());
      setData(transformedData);
    }
  }, [notificationsData]);

  const filteredNotifications =
    activeTab === t('unread', 'Unread') ? data.filter((n) => !n.read) : data;

  const markAllAsRead = () => {
    const unreadNotificationIds = data.filter((n) => !n.read).map((n) => n.id);
    if (unreadNotificationIds.length === 0) return;

    const updatedNotifications = data.map((n) => ({ ...n, read: true }));
    setData(updatedNotifications);
    markNotificationsAsRead(unreadNotificationIds);
  };

  const markAsRead = (notificationId: string) => {
    const updatedNotifications = data.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    setData(updatedNotifications);
    markNotificationsAsRead([notificationId]);
  };

  const categoryOrder = [
    t('today', 'Today'),
    t('yesterday', 'Yesterday'),
    t('previous7Days', 'Previous 7 days'),
    t('previous30Days', 'Previous 30 days'),
    t('older', 'Older'),
  ];

  return (
    <div className="mx-6 min-h-screen max-w-[1440px] md:mx-auto">
      <div className="pb-36 pt-8">
        <h2 className="text-2xl font-semibold text-[#101828]">
          {t('notifications', 'Notifications')}
        </h2>
        <p className="text-base text-[#525252]">
          {t(
            'notificationsDescription',
            'Stay informed about important updates and insights tailored to your projects and interests.'
          )}
        </p>

        <div className="mt-5 flex items-center gap-x-8 border-b border-[#F5F5F5]">
          <button
            onClick={() => setActiveTab(t('all', 'All') as 'All' | 'Unread')}
            className={`px-1 pb-4 text-sm ${
              activeTab === t('all', 'All')
                ? 'border-b-2 border-[#FFC726] font-semibold text-[#101828]'
                : 'border-b-2 border-transparent text-[#525252]'
            }`}
          >
            {t('all', 'All')}
          </button>
          <button
            onClick={() => setActiveTab(t('unread', 'Unread') as 'All' | 'Unread')}
            className={`px-1 pb-4 text-sm ${
              activeTab === t('unread', 'Unread')
                ? 'border-b-2 border-[#FFC726] font-semibold text-[#101828]'
                : 'border-b-2 border-transparent text-[#525252]'
            }`}
          >
            {t('unread', 'Unread')} ({data.filter((n) => !n.read).length})
          </button>
          <button
            onClick={markAllAsRead}
            disabled={data.filter((n) => !n.read).length === 0}
            className="flex cursor-pointer items-center gap-x-2 border-b-2 border-transparent px-1 pb-4 text-base text-[#525252] hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/quill_checkmark-double.svg`}
              alt="notification bell icon"
            />
            <span>{t('markAllAsRead', 'Mark all as read')}</span>
          </button>
        </div>

        <div className="mt-7 min-h-[70vh] rounded-2xl border border-[#E5E5E5] bg-white px-4 pb-[107px]">
          {filteredNotifications.length === 0 ? (
            <div className="flex min-h-[60vh] flex-col items-center justify-center py-20">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/notifications-empty-state.svg`}
                alt="empty state icon"
                className="mb-4 h-[104.18px] w-[118.9px] object-contain"
              />
              <h3 className="text-2xl font-semibold text-[#161616]">
                {activeTab === t('all', 'All')
                  ? t('noNotifications', 'No Notifications')
                  : t('noUnreadNotifications', 'No Unread Notifications')}
              </h3>
              <p className="mt-2.5 text-sm text-[#7F7F7F]">
                {activeTab === t('all', 'All')
                  ? t('noNotificationsMessage', 'You do not have any notifications yet.')
                  : t('noUnreadNotificationsMessage', 'You do not have any unread notifications.')}
              </p>
            </div>
          ) : (
            categoryOrder.map((category) => {
              const categoryNotifications = filteredNotifications.filter(
                (notification) => notification.day === category
              );

              if (categoryNotifications.length === 0) return null;

              return (
                <div key={category}>
                  <h3 className="mb-3 mt-6 text-sm text-[#8D8D8D]">{category}</h3>
                  {categoryNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`border-b border-[#F5F5F5] pb-4 pt-6 xl:flex xl:justify-between ${!notification.read ? 'cursor-pointer' : ''}`}
                      onClick={() => !notification.read && markAsRead(notification.id)}
                    >
                      <div className="flex items-start gap-x-6 xl:max-w-[85%]">
                        <img
                          src={`${process.env.PUBLIC_URL}/assets/images/notification-bell.svg`}
                          alt="notification bell icon"
                        />
                        <div>
                          <div className="flex items-center gap-x-2">
                            <h4
                              className={`text-sm font-semibold ${
                                notification.read ? 'text-[#8D8D8D]' : 'text-[#161616]'
                              }`}
                            >
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <span className="block h-2 w-2 rounded-full bg-[#DA1E28]" />
                            )}
                          </div>
                          <p className="mt-2 text-sm text-[#525252]">{notification.description}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-end gap-x-2 xl:mr-[22px] xl:mt-[unset]">
                        <img
                          src={`${process.env.PUBLIC_URL}/assets/images/clock.svg`}
                          alt="clock icon"
                          className="h-4 w-4"
                        />
                        <span className="text-sm text-[#8D8D8D]">{notification.timestamp}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
